import { createJsonSchemaFieldFormComponent } from '@gonfalon/json-schema-form/createJsonSchemaFieldFormComponent';
import { ListBoxItem } from '@launchpad-ui/components';
import type { FormAsyncValidateOrFn, FormOptions, FormValidateOrFn } from '@tanstack/react-form';
import { createFormHook } from '@tanstack/react-form';

import { fieldContext, formContext, useFormContext } from './contexts';
import { CancelButton } from './internal/form-components/CancelButton';
import { Checkbox } from './internal/form-components/Checkbox';
import { ComboBox } from './internal/form-components/ComboBox';
import { DateTimePicker } from './internal/form-components/DateTimePicker';
import { HiddenField } from './internal/form-components/HiddenField';
import { InlineSelect } from './internal/form-components/InlineSelect';
import { InlineTagGroup } from './internal/form-components/InlineTagGroup';
import { NumberField } from './internal/form-components/NumberField';
import { RadioGroup } from './internal/form-components/RadioGroup';
import { ResetButton } from './internal/form-components/ResetButton';
import { Select } from './internal/form-components/Select';
import { SubmitButton } from './internal/form-components/SubmitButton';
import { TagGroup } from './internal/form-components/TagGroup';
import { TextAreaInputField } from './internal/form-components/TextAreaInputField';
import { TextField } from './internal/form-components/TextField';
import { TextInputField } from './internal/form-components/TextInputField';

// Create JsonSchemaField bound to this form hook's context
const JsonSchemaField = createJsonSchemaFieldFormComponent(useFormContext);

const fieldComponents = {
  TextField,
  TextInputField,
  NumberField,
  TextAreaInputField,
  RadioGroup,
  Select,
  Checkbox,
  ComboBox,
  TagGroup,
  InlineTagGroup,
  InlineSelect,
  DateTimePicker,
  HiddenField,
  ListBoxItem,
} as const;

const formHook = createFormHook({
  fieldComponents,
  formComponents: {
    SubmitButton,
    CancelButton,
    ResetButton,
    JsonSchemaField,
  },
  fieldContext,
  formContext,
});

export type FieldComponents = typeof fieldComponents;

/**
 * A form instance with all generics set to `any` for convenience, modeled after TanStack Form's own
 * `AnyFormApi = FormApi<any, ...>` pattern. We can't use `AnyFormApi` directly because `useAppForm`
 * returns a `ReactFormExtendedApi` — `FormApi & ReactFormApi` — which includes the component
 * extensions injected by `createFormHook` / `withComponents`. Those aren't present on the core
 * `AnyFormApi`, so we derive the type from `useAppForm` itself.
 *
 * Use this when a shared component needs to accept any form instance without owning its specific
 * value type. TypeScript cannot implicitly widen from a specific form to `AnyAppForm` at contravariant
 * positions (e.g. `FormListeners.onChange` callback parameters), so call sites must double-cast:
 * `form={form as unknown as AnyAppForm}`.
 *
 * @see https://github.com/TanStack/form/blob/main/packages/form-core/src/FormApi.ts
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyAppForm = ReturnType<typeof formHook.useAppForm<any, any, any, any, any, any, any, any, any, any>>;

export const { useAppForm } = formHook;

export const useTypedAppForm = <TFormData, TSubmitMeta = never>(
  props: FormOptions<
    TFormData,
    FormValidateOrFn<TFormData> | undefined,
    FormValidateOrFn<TFormData> | undefined,
    FormAsyncValidateOrFn<TFormData> | undefined,
    FormValidateOrFn<TFormData> | undefined,
    FormAsyncValidateOrFn<TFormData> | undefined,
    FormValidateOrFn<TFormData> | undefined,
    FormAsyncValidateOrFn<TFormData> | undefined,
    FormValidateOrFn<TFormData> | undefined,
    TSubmitMeta
  >,
) => {
  return formHook.useAppForm<
    TFormData,
    FormValidateOrFn<TFormData> | undefined,
    FormValidateOrFn<TFormData> | undefined,
    FormAsyncValidateOrFn<TFormData> | undefined,
    FormValidateOrFn<TFormData> | undefined,
    FormAsyncValidateOrFn<TFormData> | undefined,
    FormValidateOrFn<TFormData> | undefined,
    FormAsyncValidateOrFn<TFormData> | undefined,
    FormValidateOrFn<TFormData> | undefined,
    TSubmitMeta
  >({
    ...props,
    defaultValues: props.defaultValues as TFormData,
  });
};
